import type { ReportSeverity } from '../report';
import type { CloudSecurityDirection } from './CloudPathLookupDecision';
import type { PacketHeader } from './PacketHeader';
import type {
  PathLookupEventTopic,
  PathLookupTopicEventName,
} from './PathLookupEvents';
import type { CloudPolicyRef, RulePath, SecurityType } from './Security';

export type DecisionType =
  | 'accept packet'
  | 'checkpoint'
  | 'cloud route match'
  | 'cloud vnic ip forwarding'
  | 'cloud vnic workload handoff'
  | 'decision'
  | 'destination NAT'
  | 'drop packet'
  | 'exec callback'
  | 'flood packet'
  | 'insert header'
  | 'jump to chain'
  | 'packet data match'
  | 'packet operation'
  | 'patch header'
  | 'policy based routing'
  | 'remove header'
  | 'route detail'
  | 'security check ignored'
  | 'security check'
  | 'severity event'
  | 'source NAT'
  | 'table entry match'
  | 'table entry not found'
  | 'virtual routing';

export const pathLookupTables = [
  'vAciEndpoint',
  'vAciVrf',
  'vArp',
  'vFabricPathRoutes',
  'vIpSecTunnels',
  'vIgmpGroup',
  'vIgmpSnoopingGroup',
  'vLispLocal',
  'vLispRemote',
  'vMac',
  'vMplsForwarding',
  'vMulticastRoute',
  'vNeighborDiscovery',
  'vRoute',
  'vRouteIpv6',
  'vSpbmBMac',
  'vSpbmISid',
  'vVirtualServer',
] as const;

export type EventSeverityInfo = {
  details?: string[]; // details relevant to specific info record, depends on topic and name
  name: PathLookupTopicEventName;
  severity: ReportSeverity;
  topic: PathLookupEventTopic; // topic can be derived from the event name
};

export type PathLookupDecisionEvent = {
  chain?: string;
  chainName?: string;
  // Cloud security checks only: the policy and the rule inside it that
  // decided. `decidingPolicyName` names the chain, i.e. the enforcement point.
  cloudDirection?: CloudSecurityDirection;
  cloudPolicy?: CloudPolicyRef;
  decidingPolicyName?: string;
  decidingRule?: RulePath;
  decidingRuleId?: string;
  field?: string;
  filter?: {
    // object describing values to filter in given table
    [key: string]: string | number;
  };
  header?: PacketHeader;
  headerType?: PacketHeader['type'];
  ifaceName?: string;
  index?: number;
  /** Security checks: the deciding chain's default action decided, not a rule. */
  isDefaultRule?: boolean;
  name?: string;
  patch?: Record<string, unknown>; // Partial<ApiPacketHeader>
  phase?: string;
  protocolId?: string;
  reason?: string;
  securityType?: SecurityType;
  severityInfo?: EventSeverityInfo;
  table?: (typeof pathLookupTables)[number];
  type: DecisionType;
  value?: unknown; // depends on the packet[field] type
  [key: string]: unknown; // extra information provided when accepting / dropping packet
};

export type PathLookupDecisionPhase = 'core' | 'in' | 'out';

export interface PathLookupDecision {
  chain: string;
  phase: PathLookupDecisionPhase;
  events: PathLookupDecisionEvent[];
}

export type HeaderRemovalReason =
  | 'IP sec tunnel begin'
  | 'GRE encapsulation end'
  | 'ESP encapsulation end'
  | 'VXLAN tunnel end'
  | 'NAT-T traversal end'
  | 'SPBM MAC-in-MAC decapsulation';
